import {
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
} from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../../assets/css/custom-ag-grid.css";
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import ErrorModal from "../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import CommonGrid from "../CommonGrid";
import { preventTabAndEnter } from "../../../utils/utilFunctions";


const customizedEditorStyling = {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  gap: ".1rem",
  alignItems: "center",
  resize: "vertical",
  overflow: "hidden",
  zIndex: "100",
};

const AccountData = ({ isTabVisible, handleClose, tabOpen, setSnackbar, setOption, source }) => {
  const { t } = useTranslation();
  const [rowData, setRowData] = useState([{ id: 0, key: "", value: "" }]);
  const [gridAPI, setGridApi] = useState();
  const { account, user } = useSelector((state) => state.queryParams);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deletedKeys,setDeletedKeys] = useState([])
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplyButtonEnabled, setIsApplyButtonEnabled] = useState(false);
  const dispatch = useDispatch();

  const CustomCellEditor = forwardRef(({ value, onValueChange }, ref) => {
    const inputRef = useRef();
    const divRef = useRef();
    // const [value, setValue] = useState(props.value);
  
  
    const resizeHandler = (e) => {
      const parentHeight = divRef.current.getBoundingClientRect().height;
      inputRef.current.style.height = `${parentHeight} !important`;
      inputRef.current.scrollTo({ bottom: 0 });
      inputRef.current.focus();
    };
  
  
    useEffect(() => {
      const resizeObserver = new ResizeObserver(resizeHandler);
      if (divRef.current) {
        resizeObserver.observe(divRef.current);
      }
  
      return () => {
        resizeObserver.disconnect();
      };
    }, []);
  
    return (
      <>
        <div ref={divRef} style={customizedEditorStyling}>
          <textarea
            style={{height: '100%', outline: 'none', width:'100%'}}
            ref={inputRef}
            className="cell-editor-custom-input-accountData"
            type="text"
            value={value}
            autoFocus={true}
            onKeyDown={preventTabAndEnter}
            onChange={({ target: { value }}) => onValueChange(value === '' ? null : value)}
            spellCheck={false}
          />
        </div>
      </>
    );
  });
  
  const setAccountKeys = () => {
    setIsLoading(true);
    dispatch(api.endpoints.getAccountData.initiate({ account, user },{forceRefetch:true}))
    .then(
      (res) => {
        // Check if the response has an error (network error, API error, etc.)
        if (res.error) {
          console.error("Failed to fetch account data keys:", res.error);
          // Set to one empty row when API fails (e.g., offline scenario)
          setRowData([{ id: 0, key: "", value: "" }]);
          setIsLoading(false);
          return;
        }
        
        if (res.data.current_state === "SUCCESS") {
          const dataKeysArray = res?.data?.data?.account_data_keys || [];
          if (dataKeysArray.length) {
            const newRowData = dataKeysArray
              .map((e, i) => {
                const key = Object.keys(e)?.[0];
                const value = e?.[key];
                return { 
                  id: i , 
                  key, 
                  value,
                  initalData : { key, value }
                };
              })
              .sort((a, b) => {
                return a?.key - b?.key;
              });
            // Adding 1 empty row;
            newRowData.push({ 
              id: newRowData.length , 
              key: "", 
              value: "" 
            });
            setRowData(newRowData);
          } else {
            // If no data, set one empty row
            setRowData([{ id: 0, key: "", value: "" }]);
          }
          setTimeout(() => setIsLoading(false),1000);
        } else {
          // If API response indicates failure
          console.error("API returned error state:", res.data.current_state);
          setRowData([{ id: 0, key: "", value: "" }]);
          setIsLoading(false);
        }
        console.log("Account Data Keys Fetched Successfully");
      }
    )
    .catch((err)=>{
      console.error("Failed to fetch account data keys:", err);
      // Set to one empty row when API fails (e.g., offline scenario)
      setRowData([{ id: 0, key: "", value: "" }]);
      setIsLoading(false);
    })
  };

  const isEditable = (params) => {
    return params.data.key;
  };

  const gridOptions = {
    columnDefs: [
      {
        showDeleteIconOnly:true,
        isDeleteControl: true,
        editable: false,
        deleteHandler: (data) => {
          setIsApplyButtonEnabled(true);
          if (data?.initalData) {
            setDeletedKeys(prev => [...prev,data]);
          }
        },
        showDeleteIcon: (data) => {
          return data.key || data.value ;
        },
        headerClass: "first-column-header",
      },
      {
        addNewControl: true,
        addControlLabel: t('LBL_ADD_NEW_ACCOUNT_KEY'),
        headerName: t("LBL_KEY"),
        field: "key",
        editable: true,
        resizable: false,
        placeholder: t('LBL_ADD_NEW_ACCOUNT_KEY_DESC'),
        preventTabAndEnter: true,
        suppressKeyboardEvent: (params) => true,
      },
      {
        headerName: t("LBL_FIELD_VALUE"),
        field: "value",
        editable: isEditable,
        resizable: false,
        cellEditor: CustomCellEditor,  
        suppressKeyboardEvent: (params) => true  ,
      },
    ],
  };

  const checkDuplicateKey = (id, value, gridApi) => {
    if (!value) return false;
    let duplicateFound = false;
    gridApi.forEachNode((node) => {
      if (node.data.id !== id && node.data.key?.toLowerCase() === value?.toLowerCase()) duplicateFound = true;
    });
    return duplicateFound;
  };

  const checkEmptyKey = (data, oldValue) => {
    if ((data.inititlaData && !data.key) || (!data.key && data.value)) return {isEmptyKey: true,  valueToKeep: oldValue}
    return {isEmptyKey: false}
  }

  const handleCellValueChanged = (gridOptions) => {
    if (gridOptions.newValue !== gridOptions.oldValue) setIsApplyButtonEnabled(true);
    const colId = gridOptions.column.colId;
    
    const newValue = gridOptions.data[colId];
    const rowIndex = gridOptions.rowIndex;
    var rowNode = gridOptions.api.getDisplayedRowAtIndex(rowIndex);
    if (newValue?.length > 400) {
      setErrorModal(true);
      setErrorMessage(t("BOX_ERR_OCCUR_WHILE_SAVING__TRY_AGAIN"));
      rowNode.setDataValue(colId, gridOptions.oldValue || "");
      return;
    }
    if (colId === "key") {
      const id = gridOptions.data.id;
      const newValue = gridOptions.data.key;
      const rowIndex = gridOptions.rowIndex;
      const isDuplicateKey = checkDuplicateKey(id, newValue, gridOptions.api);
      const {isEmptyKey, valueToKeep} = checkEmptyKey(gridOptions.data, gridOptions.oldValue);
      if (isDuplicateKey) {
        var rowNode = gridOptions.api.getDisplayedRowAtIndex(rowIndex);
        setErrorModal(true);
        setErrorMessage(t("BOX_NAME_ALREADY_EXISTS"));
        rowNode.setDataValue(colId, gridOptions?.oldValue || "");
      }
      else if (isEmptyKey) {
        var rowNode = gridOptions.api.getDisplayedRowAtIndex(rowIndex);
        setErrorModal(true);
        setErrorMessage(t("BOX_ENTER_NEW_NM_FOR_KEY"));
        if (valueToKeep) rowNode.setDataValue(colId, valueToKeep);
      }
    }
  };


  const applyHandler = () => {
    
    const deletedKeysArray = deletedKeys.map(ele=>ele.initalData.key);
    const data ={
      delete : deletedKeysArray,
      create : {},
      rename : {},
      update : {}
    }
    gridAPI.forEachNode((node) => {
      const key = node?.data?.key;
      const value = node?.data?.value;
      const inititlaData = node?.data?.initalData;
      // If node doesnt have initial data then it is new.
      if (!inititlaData && key) data.create[key] = value || '';
      if (inititlaData && inititlaData.key !== key) data.rename[inititlaData.key] = key;
      if (inititlaData && inititlaData.value !== value && inititlaData.key !==value) data.update[inititlaData.key] = value;
      else if (inititlaData && inititlaData.value !== value) data.update[key] = value;

    });
    setLoading(true);
    dispatch(api.endpoints.modify_account_data.initiate({
      data,
      account,
      user
    }, {forceRefetch : true}))
    .then(res => {
      if (res?.data?.current_state === "SUCCESS"){
        setSnackbar({
          isOpen: true,
          message: t('CHANGE_SUCCESS_APPLIED'),
          severity: "success"
        });
        handleClose();
        setIsApplyButtonEnabled(false);
      }
    })
    .catch(()=>{
      setSnackbar({
        isOpen: true,
        message: t('BOX_MSG_GENERAL_ERROR'),
        severity: "warning"
      });
      handleClose();
    })
    .finally(() => {
      setLoading(false);
    })
  }

  const getGridAPI = (api) => {
    setGridApi(api);
    if (api) {
      api.addEventListener('firstDataRendered', () => {
        api.setSuppressKeyboardEvent('Tab', true);
      });
    }
  }

  const onCellKeyDown = (params) => {
    if (params?.column?.colId === 'value') return;
    if (params.event.key === 'Tab' && params.event.shiftKey) {
      params.event.preventDefault();
      setTimeout(() => {
        const colId = params?.column?.colId;
        const rowIndex = params?.rowIndex;
        params?.api?.startEditingCell({
          rowIndex: colId === "key" ? rowIndex -1 : rowIndex,
          colKey: colId === "key" ? 'value' : 'key',
        });
      })
    }
    else if (params?.event?.key === 'Tab') {
      params.event.preventDefault();
      setTimeout(() => {
        const colId = params?.column?.colId;
        const rowIndex = params?.rowIndex;
        params?.api?.startEditingCell({
          rowIndex: colId === "key" ? rowIndex : rowIndex + 1,
          colKey: colId === "key" ? 'value' : 'key',
        });
      })
    }
  };



  const customDataToArray = (data) => {
    const cleanString = data.replace(/\r/g, '');
    // Split by newlines first to get rows
    const rows = cleanString.split('\n').filter(row => row.trim());
    const finalArray = [];
    
    rows.forEach((row) => {
      // Split each row by tabs to get columns
      const columns = row.split('\t');
      if (columns.length >= 2) {
        finalArray.push([columns[0].trim(), columns[1].trim()]);
      } else if (columns.length === 1 && columns[0].trim()) {
        finalArray.push([columns[0].trim(), '']);
      }
    });
    
    return finalArray;
  }

  const pasteValidator = (newData, parsedData, pastedData, currentRow = {}) => {
    const key = currentRow?.data?.key || '';
    // Process clipboard data for Excel paste operations
    const lines = pastedData.split(/[\r\n]+/).filter(line => line.trim());
    // Handle both tab-delimited and single value pastes
    const values = lines.length > 1 
      ? lines.flatMap(line => line.split('\t'))
      : pastedData.split('\t');
    
    // If pasting into value column, adjust the structure
    if (currentRow?.cell === "value" && key) {
      // First item should be treated as a value for the current key
      const adjustedValues = [...values];
      if (adjustedValues.length % 2 !== 0) {
      adjustedValues.push(''); // Ensure even number of elements for key-value pairs
      }
      return pasteValidator(newData, parsedData, pastedData, { ...currentRow, data: { key: '' } });
    }
    let finalArray = [];
    const dataStructure = key && currentRow?.cell === "value" ? ["value", "key"] : ["key", "value"]
    let obj = {}
    values?.forEach((o, i) => {
      if (o?.length > 400) {
        setErrorModal(true);
        setErrorMessage(t("BOX_ERR_OCCUR_WHILE_SAVING__TRY_AGAIN"));
        finalArray = [];
        return;
      }
      const rowKey = dataStructure.shift();
      obj[rowKey] = o;
      if (rowKey === "value"){
        finalArray.push(obj);
        obj={};
      }
      dataStructure.push(rowKey);
    });
    if (Object.keys(obj)?.length) finalArray.push(obj);
    return finalArray;
  }

  useEffect(() => {
    if (tabOpen) setAccountKeys();
    else {
      setRowData([]);
    }
  }, [tabOpen]);


  return (
    <>
      <Box
        sx={{
          padding: "1rem",
          fontSize: "14px",
          position: "relative",
          display: "flex",
          flexDirection: "column", 
          // gap: '20px',
          justifyContent : 'space-between',
          alignContent: "space-between"
        }}
      >
        <Box sx={{ textAlign: "justify" }}>
          {t("DIAG_ACCOUNT_DATA_DESC")}
          {t(
            "LBL_CREATE_ACC_KEYS_HERE_AND_SPECIFY_VALS__WILL_BE_AVAILABLE_FROM_OUT_TEM_BY_USING_REC_TYPE"
          )
            ?.split("\\n")
            .map((e, i) => {
              if (i === 0) return <span key={i}>{e?.split(".")?.[1]} </span>;
              else return <span key={i}>{e} </span>;
            })}
        </Box>
        <CommonGrid
          contextMenuId="account-data-target-id"
          fieldDelimeter={`\t`}
          delimeter={`\n`}
          customDataToArray={customDataToArray}
          pasteValidator={pasteValidator}
          showContextMenu={true}
          enableAutoBlur={false}
          rowData={rowData}
          gridOptions={gridOptions}
          containerClasses={'account-data-grid-container'}
          isLoading={isLoading}
          getGridAPI={getGridAPI}
          gridProps={{
            suppressHorizontalScroll: true,
            suppressScrollOnNewData: true,
            suppressScrollWhenPopupsAreOpen: true,
            // frameworkComponents: {
            //   customCellEditor: CustomCellEditor,
            // },
            onCellKeyDown:onCellKeyDown,
            animateRows: false,
            onCellValueChanged: handleCellValueChanged,
            enableCellTextSelection: true,
            ensureDomOrder: true,
            singleClickEdit: true
          }}
          />
        <Box className="accountData-actions margin-top-12">
          {/* <Divider></Divider> */}
          <Box className="flex space-between">
            <div></div>
            {/* <Button variant="outlined" onClick={() => setOption(6)}>
              {t("COPY_TO")}
            </Button> */}
            <Box sx={{ gap: ".5rem" }} className="flex">
              <Button size='small' onClick={applyHandler} disabled={!isApplyButtonEnabled} variant="outlined">
                {loading ? <CircularProgress size={"16px"}/> :t("APPLY_CNGS").split(" ").shift()}
              </Button>
              {source !== 'repository_management' && <Button size='small' variant="outlined" onClick={handleClose}>
                {t("CANCEL")}
              </Button>}
            </Box>
          </Box>
        </Box>
      </Box>
      <ErrorModal
        errorModal={errorModal}
        errorMessage={errorMessage}
        OkHandler={() => {
          setErrorModal(false);
        }}
      />
    </>
  );
};

export default AccountData;
